import moment from "moment";
import { forwardRef, useCallback, useImperativeHandle, useMemo, useState } from "react";
import { ListGroup, ListGroupItem, Table } from "reactstrap";
import useXlsx from "../../hook/useXlsx";
import { Pageable } from "../../types/common";
import { ISearchUsersResult } from "../../types/user";
import { trimToEmpty } from "../../utils/commons";
import { fullNameAutoFormat } from "../../utils/format-fullname";
import { phoneNumberAutoFormat } from "../../utils/format-phone";
import OmsToolTip from "../common/oms-tooltip/oms-tooltip";
import PaginationBar from "../common/pagination-bar";
import TableHeaders, { TableHeaderInfo } from "../common/table-header";
import './style.scss';

export interface OmsUsersTableRef {
  export: (filename?: string) => void;
}

interface UserInfo extends ISearchUsersResult {
  displayName: string;
  scopeNames: string;
}

const TABLE_HEADERS: TableHeaderInfo<UserInfo>[] = [
  { header: 'User Name', accessor: 'displayName', presorted: 'asc', cssClass: "name-column" },
  { header: 'Phone', accessor: 'phoneNumbers', sortable: true, cssClass: "phone-column" },
  { header: 'Email', accessor: 'email', sortable: true, cssClass: "email-column" },
  { header: 'Organization Scope', accessor: 'scopeNames', sortable: true }
];

const formatData = (data?: ISearchUsersResult[]): UserInfo[] | undefined =>
  data?.map(item => ({
    ...item,
    displayName: fullNameAutoFormat(item.firstName, item.middleName, item.lastName),
    phoneNumbers: item.phoneNumbers.map(phoneNumber => phoneNumberAutoFormat(phoneNumber)),
    scopeNames: item.assignedRoles.map(ar => ar.orgScope?.name).join('\n')
  }) as UserInfo);

const OmsUsersTable = forwardRef<OmsUsersTableRef, Pageable<ISearchUsersResult>>((props, ref) => {
  const { pageSize, data } = props;

  const formattedData = useMemo(() => formatData(data), [data]);

  const [sortedData, setSortedData] = useState<UserInfo[]>([]);
  const [pagedData, setPagedData] = useState<UserInfo[]>([]);

  const [exportXlsx] = useXlsx();

  const handleExport = useCallback((filename?: string) => {
    if (!sortedData) return;

    const headers = [TABLE_HEADERS.map(c => c.header)];

    const rows = sortedData.map(item => [
      item.displayName,
      item.phoneNumbers.join('\n'),
      item.email,
      item.scopeNames
    ]);

    const footers = [
      [], // blank row
      ['Total results:', sortedData.length]
    ];

    exportXlsx([...headers, ...rows, ...footers],
      trimToEmpty(filename) || `oms-users-${moment(new Date()).format('YYYYMMDDHHmmss')}`,
      true);
  }, [sortedData, exportXlsx]);

  useImperativeHandle(ref, () => ({
    export(filename?: string): void {
      handleExport(filename);
    }
  }));

  return (
    <section aria-label="Search results">
      <Table striped className="fixed">
        <TableHeaders headers={TABLE_HEADERS} sortableData={formattedData} onSort={setSortedData} />

        {formattedData && formattedData.length > 0
          ?
          <tbody>
            {pagedData.map((item) =>
              <tr key={item.id} className={item.displayOrder < 0 ? 'row-pinned' : undefined}>
                <th scope="row">{item.displayName}</th>
                <td>
                  <ListGroup flush>
                    {item.phoneNumbers.map((phone, i) =>
                      <ListGroupItem key={i}>{phone}</ListGroupItem>
                    )}
                  </ListGroup>
                </td>
                <td>{item.email}</td>
                <td>
                  <ListGroup flush>
                    {item.assignedRoles.map(ar => ar.orgScope).map(scope => scope &&
                      <ListGroupItem key={scope.id}>
                        <OmsToolTip orgId={scope.id}>
                          {scope.name}
                        </OmsToolTip>
                      </ListGroupItem>
                    )}
                  </ListGroup>
                </td>
              </tr>
            )}
          </tbody>
          :
          <tfoot>
            <tr>
              <td colSpan={TABLE_HEADERS.length} className="text-center">
                {formattedData?.length === 0
                  ? "Sorry, we couldn't find any results matching your search criteria."
                  : 'Search results will appear in this table.'
                }
              </td>
            </tr>
          </tfoot>
        }
      </Table>

      <PaginationBar data={sortedData} pageSize={pageSize} onPageChange={setPagedData} />
    </section>
  );
});

export default OmsUsersTable;
